import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { GoogleMap, Polygon, useLoadScript } from '@react-google-maps/api';
import { getRemoteConfig } from 'firebase/remote-config';

import { apiClient, getApiEndpoint } from '../../../../api/tools/tools';
import { getFirestoreValue } from '../../../../utils/firestore/tools';
import { ReactComponent as CloseIcon } from '../../../../icons/close.svg';
import { ReactComponent as SearchIcon } from '../../../../icons/search.svg';

import './polygon-zone-manager.scss';

// ─── Constants ────────────────────────────────────────────────────────────────

const MAP_LIBRARIES = ['places'];
const MAP_CONTAINER_STYLE = { width: '100%', height: '100%' };

const LEVEL_ORDER = ['suburb', 'district', 'city', 'county', 'state', 'country'];
const LEVEL_LABEL = {
  suburb: 'Suburb',
  district: 'District',
  city: 'Municipality',
  county: 'County',
  state: 'Province',
  country: 'Country'
};

const MUTED_MAP_STYLES = [
  { elementType: 'geometry', stylers: [{ color: '#edeae3' }] },
  { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#888480' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#edeae3' }] },
  { featureType: 'administrative', elementType: 'geometry.stroke', stylers: [{ color: '#c8bfb5' }] },
  { featureType: 'administrative.locality', elementType: 'labels.text.fill', stylers: [{ color: '#5a5652' }] },
  { featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi.park', elementType: 'geometry.fill', stylers: [{ color: '#d4dcca' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#f7f4ef' }] },
  { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#ddd8d0' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#f0ebe0' }] },
  { featureType: 'road.highway', elementType: 'geometry.stroke', stylers: [{ color: '#d4cdc0' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
  { featureType: 'water', elementType: 'geometry.fill', stylers: [{ color: '#b8cdd6' }] }
];

// ─── Geometry helpers ─────────────────────────────────────────────────────────

const toGooglePaths = (polygonJson) => {
  try {
    const rings = JSON.parse(polygonJson);
    return rings.map((ring) => ring.map(([lat, lng]) => ({ lat, lng })));
  } catch {
    return [];
  }
};

const geoapifyGeometryToRings = (geometry) => {
  if (!geometry) return null;
  if (geometry.type === 'Polygon') {
    return geometry.coordinates.map((ring) => ring.map(([lng, lat]) => [lat, lng]));
  }
  if (geometry.type === 'MultiPolygon') {
    const largest = geometry.coordinates.reduce(
      (best, poly) => (poly[0].length > best[0].length ? poly : best),
      geometry.coordinates[0]
    );
    return largest.map((ring) => ring.map(([lng, lat]) => [lat, lng]));
  }
  return null;
};

const ringCentre = (ring) => {
  if (!ring?.length) return null;
  const minLat = ring.reduce((a, [lat]) => Math.min(a, lat), Infinity);
  const maxLat = ring.reduce((a, [lat]) => Math.max(a, lat), -Infinity);
  const minLng = ring.reduce((a, [, lng]) => Math.min(a, lng), Infinity);
  const maxLng = ring.reduce((a, [, lng]) => Math.max(a, lng), -Infinity);
  return { lat: (minLat + maxLat) / 2, lng: (minLng + maxLng) / 2 };
};

// ─── Map component ────────────────────────────────────────────────────────────

const ZoneMap = ({ googleApiKey, zones, previewPaths, previewType, hoverPaths }) => {
  const { isLoaded } = useLoadScript({ googleMapsApiKey: googleApiKey, libraries: MAP_LIBRARIES });
  const mapRef = useRef(null);
  const [mapReady, setMapReady] = useState(false);

  const fitToPaths = useCallback((paths) => {
    if (!mapRef.current || !paths?.length) return;
    const bounds = new window.google.maps.LatLngBounds();
    paths.flat().forEach((p) => bounds.extend(p));
    if (!bounds.isEmpty()) mapRef.current.fitBounds(bounds, 48);
  }, []);

  useEffect(() => { fitToPaths(previewPaths); }, [previewPaths, fitToPaths]);
  useEffect(() => { fitToPaths(hoverPaths); }, [hoverPaths, fitToPaths]);

  useEffect(() => {
    if (!mapReady || !zones.length || previewPaths || hoverPaths) return;
    const allPaths = zones.flatMap((z) => toGooglePaths(z.polygonJson));
    fitToPaths(allPaths);
  }, [mapReady, zones]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isLoaded) {
    return <div className="pzm-map-shell pzm-map-shell--loading">Loading map…</div>;
  }

  const activePaths = hoverPaths ?? previewPaths;
  const activeType = hoverPaths ? null : previewType; // hover is neutral blue

  return (
    <div className="pzm-map-shell">
      {zones && (
          <GoogleMap
              mapContainerStyle={MAP_CONTAINER_STYLE}
              zoom={10}
              center={{ lat: -25.8853, lng: 28.1037 }}
              onLoad={(m) => { mapRef.current = m; }}
              onIdle={() => setMapReady(true)}
              options={{ clickableIcons: false, styles: MUTED_MAP_STYLES }}
          >
            {mapReady && zones.map((zone) => {
              const paths = toGooglePaths(zone.polygonJson);
              if (!paths.length) return null;
              const isExclude = zone.zoneType === 'EXCLUDE';
              return (
                  <Polygon
                      key={zone.id}
                      paths={paths}
                      options={{
                        strokeColor: isExclude ? '#ef4444' : '#22c55e',
                        strokeOpacity: 0.9,
                        strokeWeight: 2,
                        fillColor: isExclude ? '#ef4444' : '#22c55e',
                        fillOpacity: 0.2
                      }}
                  />
              );
            })}

            {mapReady && activePaths && (
                <Polygon
                    paths={activePaths}
                    options={{
                      strokeColor: activeType === 'EXCLUDE' ? '#ef4444' : '#3b82f6',
                      strokeOpacity: 0.8,
                      strokeWeight: 2,
                      fillColor: activeType === 'EXCLUDE' ? '#ef4444' : '#3b82f6',
                      fillOpacity: activeType ? 0.15 : 0.08
                    }}
                />
            )}
          </GoogleMap>
      )}

    </div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

export const PolygonZoneManager = ({ zones = [], onAdd, onRemove, saving }) => {
  const [googleApiKey, setGoogleApiKey] = useState('');

  const [searchText, setSearchText] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const [levels, setLevels] = useState([]); // { label, type, rings, paths }
  const [loadingLevels, setLoadingLevels] = useState(false);
  const [levelsError, setLevelsError] = useState('');

  const [selectedLevel, setSelectedLevel] = useState(null); // one of levels[]
  const [hoveredLevel, setHoveredLevel] = useState(null);
  const [zoneType, setZoneType] = useState('INCLUDE');
  const [hiddenZoneIds, setHiddenZoneIds] = useState(new Set());

  const debounceRef = useRef(null);

  useEffect(() => {
    getFirestoreValue('google_api_key').then(setGoogleApiKey);
  }, []);

  const getApiUrl = useCallback(async () => getApiEndpoint(getRemoteConfig()), []);

  // ── Search ──────────────────────────────────────────────────────────────────

  const resetSelection = () => {
    setLevels([]);
    setSelectedLevel(null);
    setHoveredLevel(null);
    setLevelsError('');
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchText(value);
    setSearchResults([]);
    resetSelection();

    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!value.trim() || value.length < 2) return;

    debounceRef.current = setTimeout(async () => {
      try {
        setSearching(true);
        const apiUrl = await getApiUrl();
        const res = await apiClient.get(`${apiUrl}/delivery/zones/polygons/search`, {
          params: { text: value }
        });
        setSearchResults(res.data?.features ?? []);
      } catch {
        // silent — search failure just shows no results
      } finally {
        setSearching(false);
      }
    }, 350);
  };

  // ── Select result → fetch all boundary levels ────────────────────────────────

  const handleSelectResult = async (feature) => {
    setSearchResults([]);
    setSearchText(feature.properties?.name ?? '');
    resetSelection();

    const lng = feature.geometry?.coordinates?.[0];
    const lat = feature.geometry?.coordinates?.[1];
    if (!lat || !lng) { setLevelsError('No coordinates for this result.'); return; }

    try {
      setLoadingLevels(true);
      const apiUrl = await getApiUrl();
      const res = await apiClient.get(`${apiUrl}/delivery/zones/polygons/boundary`, {
        params: { lat, lng }
      });

      const features = res.data?.features ?? [];
      const parsed = features
        .map((f) => {
          const rings = geoapifyGeometryToRings(f.geometry);
          if (!rings) return null;
          const paths = rings.map((ring) => ring.map(([lt, ln]) => ({ lat: lt, lng: ln })));
          const rt = f.properties?.result_type ?? '';
          return {
            key: f.properties?.place_id ?? rt,
            label: f.properties?.name ?? f.properties?.formatted ?? rt,
            type: rt,
            rings,
            paths,
            centre: ringCentre(rings[0])
          };
        })
        .filter(Boolean)
        .sort((a, b) => LEVEL_ORDER.indexOf(a.type) - LEVEL_ORDER.indexOf(b.type));

      if (!parsed.length) {
        setLevelsError('No polygon boundaries found. Try a different search.');
        return;
      }

      setLevels(parsed);
      // Auto-select the most specific level
      setSelectedLevel(parsed[0]);
    } catch {
      setLevelsError('Failed to load boundaries. Try again.');
    } finally {
      setLoadingLevels(false);
    }
  };

  // ── Save ─────────────────────────────────────────────────────────────────────

  const handleSave = () => {
    if (!selectedLevel) return;
    const centre = selectedLevel.centre;
    onAdd(
      {
        label: selectedLevel.label,
        formatted: selectedLevel.label,
        geoapifyPlaceId: selectedLevel.key,
        centerLat: centre?.lat ?? null,
        centerLng: centre?.lng ?? null,
        polygonJson: JSON.stringify(selectedLevel.rings),
        zoneType
      },
      () => {
        resetSelection();
        setSearchText('');
      }
    );
  };

  const handleCancel = () => {
    resetSelection();
    setSearchText('');
  };

  const toggleZoneVisibility = (id) => {
    setHiddenZoneIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const allVisible = zones.length > 0 && hiddenZoneIds.size === 0;
  const allHidden = zones.length > 0 && hiddenZoneIds.size === zones.length;

  // Paths to show on map
  const visibleZones = useMemo(() => zones.filter((z) => !hiddenZoneIds.has(z.id)), [hiddenZoneIds, zones]);
  const previewPaths = selectedLevel?.paths ?? null;
  const hoverPaths = hoveredLevel && hoveredLevel !== selectedLevel ? hoveredLevel.paths : null;

  return (
    <div className="polygon-zone-manager">

      {/* ── Left column ─────────────────────────────────────────────────────── */}
      <div className="pzm-left">

        {/* Search */}
        <div className="pzm-search">
          <div className="pzm-search__input-wrap">
            <SearchIcon className="pzm-search__icon" width={15} height={15} />
            <input
              type="text"
              className="pzm-search__input"
              placeholder="Search suburb, area or city…"
              value={searchText}
              onChange={handleSearchChange}
            />
            {searching && <span className="pzm-search__spinner" />}
            {searchText && !searching && (
              <button
                type="button"
                className="pzm-search__clear"
                onClick={handleCancel}
              >
                <CloseIcon width={12} height={12} />
              </button>
            )}
          </div>

          {searchResults.length > 0 && (
            <div className="pzm-search__results">
              {searchResults.map((f, i) => (
                <button
                  key={f.properties?.place_id ?? i}
                  type="button"
                  className="pzm-search__result"
                  onClick={() => handleSelectResult(f)}
                >
                  <span className="pzm-search__result-name">
                    {f.properties?.name ?? f.properties?.formatted}
                  </span>
                  <span className="pzm-search__result-sub">{f.properties?.formatted}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {loadingLevels && <p className="pzm-status">Fetching boundaries…</p>}
        {levelsError && <p className="pzm-error">{levelsError}</p>}

        {/* Boundary level chips */}
        {levels.length > 0 && (
          <div className="pzm-levels">
            <span className="pzm-levels__label">Boundary level</span>
            <div className="pzm-levels__chips">
              {levels.map((level) => (
                <button
                  key={level.key}
                  type="button"
                  className={`pzm-levels__chip ${selectedLevel?.key === level.key ? 'pzm-levels__chip--selected' : ''}`}
                  onClick={() => setSelectedLevel(level)}
                  onMouseEnter={() => setHoveredLevel(level)}
                  onMouseLeave={() => setHoveredLevel(null)}
                >
                  <span className="pzm-levels__chip-type">{LEVEL_LABEL[level.type] ?? level.type}</span>
                  <span className="pzm-levels__chip-name">{level.label}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Include / Exclude + Save */}
        {selectedLevel && (
          <div className="pzm-confirm">
            <div className="pzm-confirm__area">
              <span className="pzm-confirm__area-name">{selectedLevel.label}</span>
              <span className="pzm-confirm__area-type">
                {LEVEL_LABEL[selectedLevel.type] ?? selectedLevel.type}
              </span>
            </div>

            <div className="pzm-confirm__toggle">
              <button
                type="button"
                className={`pzm-confirm__toggle-btn pzm-confirm__toggle-btn--include ${zoneType === 'INCLUDE' ? 'active' : ''}`}
                onClick={() => setZoneType('INCLUDE')}
              >
                Include
              </button>
              <button
                type="button"
                className={`pzm-confirm__toggle-btn pzm-confirm__toggle-btn--exclude ${zoneType === 'EXCLUDE' ? 'active' : ''}`}
                onClick={() => setZoneType('EXCLUDE')}
              >
                Exclude
              </button>
            </div>

            <div className="pzm-confirm__actions">
              <button
                type="button"
                className="pzm-confirm__save"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Saving…' : 'Save zone'}
              </button>
              <button type="button" className="pzm-confirm__cancel" onClick={handleCancel}>
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Zone list */}
        {zones.length > 0 ? (
          <div className="pzm-zone-list">
            <div className="pzm-zone-list__header">
              <span className="pzm-zone-list__heading">Saved zones</span>
              <div className="pzm-zone-list__bulk">
                <button
                  type="button"
                  className="pzm-zone-list__bulk-btn"
                  disabled={allVisible}
                  onClick={() => setHiddenZoneIds(new Set())}
                >
                  All
                </button>
                <button
                  type="button"
                  className="pzm-zone-list__bulk-btn"
                  disabled={allHidden}
                  onClick={() => setHiddenZoneIds(new Set(zones.map((z) => z.id)))}
                >
                  None
                </button>
              </div>
            </div>
            {zones.map((zone) => {
              const visible = !hiddenZoneIds.has(zone.id);
              return (
                <div key={zone.id} className={`pzm-zone-row ${!visible ? 'pzm-zone-row--hidden' : ''}`}>
                  <input
                    type="checkbox"
                    className="pzm-zone-item__checkbox"
                    checked={visible}
                    onChange={() => toggleZoneVisibility(zone.id)}
                  />
                  <div className={`pzm-zone-item pzm-zone-item--${zone.zoneType?.toLowerCase()}`}>
                    <div className="pzm-zone-item__dot" />
                    <div className="pzm-zone-item__info">
                      <span className="pzm-zone-item__name">{zone.label}</span>
                    </div>
                    <span className="pzm-zone-item__badge">
                      {zone.zoneType === 'EXCLUDE' ? 'Exclude' : 'Include'}
                    </span>
                    <button
                      type="button"
                      className="pzm-zone-item__remove"
                      onClick={() => onRemove(zone.id)}
                    >
                      <CloseIcon width={11} height={11} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          !levels.length && !loadingLevels && (
            <p className="pzm-empty">Search for an area to add your first delivery zone.</p>
          )
        )}
      </div>

      {/* ── Right column — map ───────────────────────────────────────────────── */}
      <div className="pzm-right">
        {googleApiKey ? (
          <ZoneMap
            googleApiKey={googleApiKey}
            zones={visibleZones}
            previewPaths={previewPaths}
            previewType={zoneType}
            hoverPaths={hoverPaths}
          />
        ) : (
          <div className="pzm-map-shell pzm-map-shell--loading">Loading map…</div>
        )}
        <div className="pzm-map-legend">
          <span className="pzm-map-legend__item pzm-map-legend__item--include">Include zone</span>
          <span className="pzm-map-legend__item pzm-map-legend__item--exclude">Exclude zone</span>
          {(previewPaths || hoverPaths) && (
            <span className="pzm-map-legend__item pzm-map-legend__item--preview">Preview</span>
          )}
        </div>
      </div>

    </div>
  );
};
